import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, X, AtSign } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  USERNAME_MAX,
  USERNAME_MIN,
  checkUsernameAvailable,
  fetchUsernameRenameStatus,
  renameUsername,
  validateUsernameFormat,
  type UsernameRenameStatus,
} from "@/lib/username";
import { useUsername } from "@/hooks/useUsername";

import { useWorkspace } from "@/hooks/useWorkspace";

type Status = "idle" | "checking" | "available" | "taken" | "invalid";

export const UsernameCard = () => {
  const { refresh } = useUsername();
  const { activePersonaId } = useWorkspace();
  const [info, setInfo] = useState<UsernameRenameStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!activePersonaId) { setInfo(null); setLoading(false); return; }
    setLoading(true);
    const r = await fetchUsernameRenameStatus(activePersonaId);
    setInfo(r);
    setLoading(false);
  }, [activePersonaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const validation = useMemo(() => validateUsernameFormat(value), [value]);
  const debounceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!validation.ok) {
      setStatus(value.length === 0 ? "idle" : "invalid");
      setErrorMessage(
        value.length === 0
          ? null
          : (validation as { ok: false; message: string }).message,
      );
      return;
    }
    if (info?.username && validation.normalized === info.username) {
      setStatus("invalid");
      setErrorMessage("That's already your handle.");
      return;
    }
    setStatus("checking");
    setErrorMessage(null);
    debounceRef.current = window.setTimeout(async () => {
      const ok = await checkUsernameAvailable(validation.normalized);
      setStatus(ok ? "available" : "taken");
      if (!ok) setErrorMessage("That username is taken or reserved.");
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, validation, open, info?.username]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== "available" || !validation.ok || !activePersonaId) return;
    setSubmitting(true);
    try {
      await renameUsername(activePersonaId, validation.normalized);
      toast.success(`You're now @${validation.normalized}`);
      setOpen(false);
      setValue("");
      setStatus("idle");
      await Promise.all([load(), refresh()]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Rename failed.";
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AtSign className="h-4 w-4" /> Username
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : !info?.username ? (
          <p className="text-muted-foreground">
            You haven't claimed a handle yet.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-mono text-base font-medium">
                  @{info.username}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used for your affiliate link and your future public profile.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={!info.canRenameNow}
                onClick={() => {
                  setValue("");
                  setStatus("idle");
                  setErrorMessage(null);
                  setOpen(true);
                }}
              >
                Rename
              </Button>
            </div>
            {!info.canRenameNow && info.nextEligibleAt && (
              <p className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
                You can rename again on{" "}
                <span className="text-foreground">
                  {info.nextEligibleAt.toLocaleDateString()}
                </span>
                . Renames are limited to once every 12 months.
              </p>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !submitting && setOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename your handle</DialogTitle>
            <DialogDescription>
              Choose carefully — you won't be able to change this again for{" "}
              <span className="font-medium text-foreground">12 months</span>.
              Your current handle (<span className="font-mono">@{info?.username}</span>) will be
              reserved for 90 days after the change to prevent impersonation, then released.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <div className="space-y-2">
              <Label htmlFor="rename-username">New username</Label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
                  @
                </span>
                <Input
                  id="rename-username"
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={value}
                  onChange={(e) =>
                    setValue(e.target.value.toLowerCase().slice(0, USERNAME_MAX))
                  }
                  className="pl-7 pr-9"
                  placeholder="newhandle"
                  disabled={submitting}
                  aria-invalid={status === "invalid" || status === "taken"}
                />
                <span className="absolute inset-y-0 right-3 flex items-center">
                  {status === "checking" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : status === "available" ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : status === "taken" || status === "invalid" ? (
                    <X className="h-4 w-4 text-destructive" />
                  ) : null}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {USERNAME_MIN}–{USERNAME_MAX} lowercase letters and numbers. Must include at least one letter.
              </p>
              {errorMessage ? (
                <p className="text-sm text-destructive">{errorMessage}</p>
              ) : status === "available" ? (
                <p className="text-sm text-primary">
                  @{validation.ok ? validation.normalized : ""} is available.
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={status !== "available" || submitting}
              >
                {submitting ? "Renaming…" : "Rename handle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


